import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ApiErrorDetails from 'components/ApiErrorDetails/index.tsx';
import Back from 'components/Back/index.tsx';
import IterationDetailsForm from 'components/forms/IterationDetails/IterationDetailsForm.tsx';
import useProtectRoute from 'hooks/useProtectRoute.ts';
import useCreateIteration from 'queries/mutate/createIteration.ts';
import { IterationDetailsFormValues } from 'schemas/Iteration.schema.ts';
import { ApiError } from 'schemas/ApiError.schema';
import { API_CREATE_ITERATION_ERROR } from 'constants/errorMessages';
import { IIterationLevelParams } from 'utils/parameterUtils';
import useFetchSpecificCampaign from 'queries/fetch/fetchSpecificCampaign';
import useFetchTemplateForCampaign from 'queries/fetch/fetchTemplateForCampaign.ts';

const NEW_ITERATION: IterationDetailsFormValues = {
  Name: '',
  IterationDate: '',
  CommsType: 'I',
  ApprovalMinimum: 0,
  ApprovalMaximum: 0,
  Type: 'A',
  IterationCohorts: [],
  IterationRules: [],
};

function CreateIterationDetailsPage() {
  const [error, setError] = useState<ApiError | null>(null);
  const { teamId, campaignId } = useParams() as IIterationLevelParams;
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  useProtectRoute();
  const navigate = useNavigate();

  const createIteration = useCreateIteration();
  const fetchSpecificCampaign = useFetchSpecificCampaign({ teamId, campaignId });
  const campaignTemplate = useFetchTemplateForCampaign({
    teamId,
    campaignId,
    isEnabled: Boolean(fetchSpecificCampaign.data?.CampaignConfig.Template),
  });

  const handleSuccessfulSubmission = async (iteration: IterationDetailsFormValues) => {
    try {
      setIsSubmitting(true);

      const createResult = await createIteration.mutateAsync({
        teamId: teamId!,
        campaignId: campaignId!,
        iteration: iteration!,
      });

      await fetchSpecificCampaign.refetch();

      const redirectPage = searchParams.get('redirectPage');
      if (redirectPage) {
        navigate(redirectPage);
        return;
      }

      navigate(`/${teamId}/campaign/${campaignId}/iteration/${createResult.iteration_details.ID!}/key-details/confirmation`);
    } catch {
      setError({ message: API_CREATE_ITERATION_ERROR });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className="nhsuk-u-margin-bottom-6">
        <Back label="Campaign Summary" backTo={`/${teamId}/campaign/${campaignId}/summary`} />
      </div>
      <IterationDetailsForm
        onSuccessfulSubmission={handleSuccessfulSubmission}
        isSubmitting={isSubmitting}
        iteration={NEW_ITERATION}
        template={campaignTemplate.data}
      />
      <ApiErrorDetails apiError={error || createIteration.error} />
    </div>
  );
}
export default CreateIterationDetailsPage;
